import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
} from "@mui/material";
import { ReconStatus, ReconTradeRow } from "../../../Types/Reconautomation2.types";




const STATUS_META: Record<
  ReconStatus,
  { label: string; color: "success" | "error" | "warning" | "default" }
> = {
  MATCHED: { label: "Matched", color: "success" },
  MISMATCH_PRICE: { label: "Price Mismatch", color: "error" },
  MISMATCH_MARKUP: { label: "Markup Mismatch", color: "warning" },
  MISMATCH_PRICE_AND_MARKUP: { label: "Price & Markup Mismatch", color: "error" },
  UNMATCHED_BGM: { label: "Unmatched BGM", color: "warning" },
  UNMATCHED_ACERS: { label: "Unmatched ACERS", color: "warning" },
  CANON_FAILED: { label: "Canonicalisation Failed", color: "default" },
  FAILED_MISSING_CONTRACT_MAPPING: {
    label: "Missing Contract Mapping",
    color: "warning",
  },
  FAILED_MISSING_CLIENT_MAPPING: {
    label: "Missing Client Mapping",
    color: "warning",
  },
  SUCCESS: { label: "Success", color: "success" },
};


interface ValueRowProps {
  label: string;
  bgm: string | number | null;
  acers: string | number | null;
  highlight?: boolean; // red highlight if mismatch
}

const ValueRow: React.FC<ValueRowProps> = ({ label, bgm, acers, highlight }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      py: 0.8,
      px: 1,
      borderRadius: "4px",
      backgroundColor: highlight ? "rgba(211,47,47,0.06)" : "transparent",
      border: highlight ? "1px solid rgba(211,47,47,0.2)" : "1px solid transparent",
      mb: 0.5,
    }}
  >
    <Typography
      sx={{ width: 160, fontSize: "0.72rem", color: "#666", fontWeight: 500 }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, display: "flex", gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: "0.65rem", color: "#999", mb: 0.2 }}>
          BGM
        </Typography>
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: highlight ? "#c62828" : "#222",
          }}
        >
          {bgm ?? "—"}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: "0.65rem", color: "#999", mb: 0.2 }}>
          ACERS
        </Typography>
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: highlight ? "#c62828" : "#222",
          }}
        >
          {acers ?? "—"}
        </Typography>
      </Box>
    </Box>
    {highlight && (
      <Typography sx={{ fontSize: "0.65rem", color: "#c62828", ml: 1 }}>
        🔴 mismatch
      </Typography>
    )}
  </Box>
);


interface TradeDetailModalProps {
  open: boolean;
  onClose: () => void;
  trade: ReconTradeRow | null;
}

const TradeDetailModal: React.FC<TradeDetailModalProps> = ({
  open,
  onClose,
  trade,
}) => {
  if (!trade) return null;

  const meta = STATUS_META[trade.reconciliation_status];

  const priceMismatch =
    trade.reconciliation_status === "MISMATCH_PRICE" ||
    trade.reconciliation_status === "MISMATCH_PRICE_AND_MARKUP";
  const markupMismatch =
    trade.reconciliation_status === "MISMATCH_MARKUP" ||
    trade.reconciliation_status === "MISMATCH_PRICE_AND_MARKUP";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          border: "1px solid #e0e0e0",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: "0.72rem", color: "#999", mb: 0.3 }}
          >
            Trade Detail
          </Typography>
          <Typography
            sx={{ fontSize: "1rem", fontWeight: 700, color: "#111" }}
          >
            {trade.trade_id}
          </Typography>
        </Box>
        <Chip
          label={meta.label}
          color={meta.color}
          size="small"
          sx={{ fontSize: "0.68rem", height: 22 }}
        />
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* ── Basic info ── */}
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#888",
            textTransform: "uppercase",
            letterSpacing: 1,
            mb: 1,
          }}
        >
          Trade Info
        </Typography>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {[
            { label: "Source", value: trade.source_system },
            { label: "Client", value: trade.client },
            { label: "Contract", value: trade.contract },
            { label: "Quantity", value: trade.quantity },
          ].map(({ label, value }) => (
            <Grid item xs={6} key={label}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "4px",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #eee",
                }}
              >
                <Typography sx={{ fontSize: "0.62rem", color: "#999", mb: 0.2 }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                  {value ?? "—"}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* ── Comparison values ── */}
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#888",
            textTransform: "uppercase",
            letterSpacing: 1,
            mb: 1,
          }}
        >
          Value Comparison
        </Typography>
        <ValueRow
          label="Price"
          bgm={trade.bgm_price}
          acers={trade.acers_price}
          highlight={priceMismatch}
        />
        <ValueRow
          label="Markup"
          bgm={trade.markup_bgm}
          acers={trade.markup_acers}
          highlight={markupMismatch}
        />

        {/* ── Break reason ── */}
        {trade.break_reason && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: "#888",
                textTransform: "uppercase",
                letterSpacing: 1,
                mb: 1,
              }}
            >
              Break Reason
            </Typography>
            <Box
              sx={{
                p: 1.5,
                backgroundColor: "rgba(211,47,47,0.05)",
                border: "1px solid rgba(211,47,47,0.2)",
                borderRadius: "4px",
              }}
            >
              {/* <Typography sx={{ fontSize: "0.78rem", color: "#c62828" }}>
                {trade.break_reason}
              </Typography> */}
            </Box>
          </>
        )}

        {/* ── Failure issue ── */}
        {trade.issue && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: "#888",
                textTransform: "uppercase",
                letterSpacing: 1,
                mb: 1,
              }}
            >
              Canonicalisation Issue
            </Typography>
            <Box
              sx={{
                p: 1.5,
                backgroundColor: "rgba(245,124,0,0.05)",
                border: "1px solid rgba(245,124,0,0.25)",
                borderRadius: "4px",
              }}
            >
              <Typography sx={{ fontSize: "0.78rem", color: "#e65100" }}>
                {trade.issue}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TradeDetailModal;